import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useTheme } from '@chakra-ui/react';

import {
  BrandTheme,
  Typography,
  ToggleTabs,
} from '@eucalyptusvc/design-system';
import { getConfig } from '@customer-frontend/config';
import { UnitSystem } from '@customer-frontend/graphql-types';
import { convertAndFormatWeight } from '@customer-frontend/utils';

export type WeightLossGraphSVG = {
  firstName?: string | null;
  initialWeight?: number | null;
  preferredUnitSystem?: UnitSystem | null;
  showDisclaimer?: boolean;
};

export const WeightLossGraphSVG = (
  props: WeightLossGraphSVG,
): React.ReactElement => {
  const config = getConfig();
  const theme = useTheme<BrandTheme>();
  const [unitSystem, setUnitSystem] = useState<UnitSystem | null>(
    props.preferredUnitSystem ?? null,
  );

  const initialWeight = Math.round(props.initialWeight ?? 100);
  const predictedWeight = Math.round(initialWeight * 0.863);
  const predictedWeightLoss = Math.round(initialWeight - predictedWeight);

  const convertedInitialWeight = convertAndFormatWeight(
    initialWeight.toString(),
    unitSystem,
  );

  const convertedPredictedWeight = convertAndFormatWeight(
    predictedWeight.toString(),
    unitSystem,
  );

  const convertedPredictedWeightLoss = convertAndFormatWeight(
    predictedWeightLoss.toString(),
    unitSystem,
  );

  let chartColor = '';
  let dotColor = '';
  let initialLabelColor = '';
  switch (config.brand) {
    case 'juniper':
    case 'juniper-uk':
    case 'juniper-de':
    case 'juniper-jp':
      chartColor = theme.colors.secondary.purple[300];
      dotColor = theme.colors.secondary.purple[100];
      initialLabelColor = theme.colors.secondary.purple[300];
      break;
    case 'pilot':
      chartColor = theme.colors.secondary.navy[300];
      dotColor = theme.colors.secondary.navy[100];
      initialLabelColor = theme.colors.secondary.navy[300];
      break;
    case 'compound-uk':
      chartColor = theme.colors.secondary.brown[300];
      dotColor = theme.colors.secondary.brown[100];
      initialLabelColor = theme.colors.secondary.brown[300];
      break;
    default:
      chartColor = theme.colors.primary[300];
      dotColor = theme.colors.primary[100];
      initialLabelColor = theme.colors.primary[300];
      break;
  }

  return (
    <div className="flex flex-col text-center gap-4">
      <div className="flex flex-col items-center bg-white border rounded border-primary-300 p-4 md:p-6 gap-3">
        <div className="pb-4 md:pb-6">
          <Typography size="md" isBold color={theme.colors.primary[600]}>
            <FormattedMessage
              defaultMessage="{hasFirstName, select, true {{firstName}, you} other {You}} could lose {predictedWeightLoss}"
              description="Predicted weight loss"
              values={{
                hasFirstName: !!props.firstName,
                firstName: props.firstName,
                predictedWeightLoss: convertedPredictedWeightLoss,
              }}
            />
          </Typography>
          <Typography size="paragraph" color={theme.colors.primary[600]}>
            <FormattedMessage
              defaultMessage="in your first 4 months with {brand},{br}based on your starting weight of {initialWeight}"
              description="Weight loss in first 4 months based on starting weight"
              values={{
                initialWeight: convertedInitialWeight,
                br: <br />,
                brand: config.title,
              }}
            />
          </Typography>
        </div>
        <svg
          width="w-full"
          viewBox="0 0 568 218"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M467.5 202L467.5 126"
            stroke={theme.colors.primary[500]}
            strokeWidth="1.5"
          />
          <path d="M21 33L21 24" stroke={chartColor} strokeWidth="1.5" />
          <path
            d="M440.5 101L440.5 77"
            stroke={theme.colors.primary[300]}
            strokeWidth="1.5"
          />
          <line
            x1="14.3198"
            y1="217"
            x2="14.3198"
            y2="25"
            stroke={theme.colors.primary[400]}
            strokeWidth="0.5"
          />
          <line
            x1="13.9641"
            y1="40.75"
            x2="566.289"
            y2="40.75"
            stroke={theme.colors.primary[400]}
            strokeWidth="0.5"
            strokeDasharray="2 2"
          />
          <line
            x1="13.9641"
            y1="94.4166"
            x2="566.289"
            y2="94.4166"
            stroke={theme.colors.primary[400]}
            strokeWidth="0.5"
            strokeDasharray="2 2"
          />
          <line
            x1="13.9641"
            y1="148.083"
            x2="566.289"
            y2="148.083"
            stroke={theme.colors.primary[400]}
            strokeWidth="0.5"
            strokeDasharray="2 2"
          />
          <line
            x1="13.9641"
            y1="201.75"
            x2="566.289"
            y2="201.75"
            stroke={theme.colors.primary[400]}
            strokeWidth="0.5"
          />
          <path
            d="M18.3055 213.5V205.85H19.1855L23.0155 211.92C23.2455 212.29 23.3555 212.54 23.3555 212.54H23.3755V205.85H24.0655V213.5H23.1755L19.3755 207.45C19.1355 207.07 19.0155 206.82 19.0155 206.82H18.9955V213.5H18.3055ZM27.9645 213.63C26.3945 213.63 25.6645 212.48 25.6645 210.88C25.6645 209.3 26.5145 208.13 27.9645 208.13C29.5445 208.13 30.2545 209.3 30.2545 210.88C30.2545 212.49 29.4245 213.63 27.9645 213.63ZM27.9645 208.67C26.9345 208.67 26.3745 209.53 26.3745 210.88C26.3745 212.23 26.8745 213.08 27.9745 213.08C29.0345 213.08 29.5345 212.23 29.5345 210.88C29.5345 209.53 29.0545 208.67 27.9645 208.67ZM32.7488 213.5L31.0988 208.26H31.8088L32.9788 212.14C33.0588 212.42 33.1288 212.69 33.1288 212.69H33.1488C33.1488 212.69 33.2288 212.41 33.3188 212.13L34.5288 208.26H35.2588L36.4688 212.13C36.5388 212.36 36.6388 212.69 36.6388 212.69H36.6588C36.6588 212.69 36.7288 212.36 36.7988 212.13L37.9688 208.26H38.6488L36.9988 213.5H36.2488L35.1588 210.01C35.0288 209.59 34.8888 209.09 34.8888 209.09H34.8688C34.8688 209.09 34.7288 209.59 34.5988 210.01L33.5088 213.5H32.7488Z"
            fill={theme.colors.neutral[700]}
          />
          <path
            d="M444.276 211.04H447.086V207H447.066C447.066 207 446.916 207.31 446.586 207.77L444.276 211.04ZM448.796 211.64H447.766V213.5H447.086V211.64H443.536V211.07L447.066 206.09H447.766V211.03H448.796V211.64ZM452.866 213.5V208.26H453.516V209.19H453.536C453.806 208.6 454.406 208.13 455.266 208.13C456.076 208.13 456.646 208.47 456.826 209.26H456.846C457.106 208.64 457.736 208.13 458.616 208.13C459.736 208.13 460.256 208.74 460.256 209.94V213.5H459.566V210.19C459.566 209.75 459.526 209.35 459.336 209.08C459.156 208.83 458.866 208.72 458.456 208.72C457.376 208.72 456.906 209.51 456.906 210.41V213.5H456.216V210.18C456.216 209.75 456.176 209.35 455.986 209.09C455.806 208.84 455.526 208.72 455.116 208.72C454.036 208.72 453.566 209.51 453.566 210.4V213.5H452.866ZM464.023 213.63C462.453 213.63 461.723 212.48 461.723 210.88C461.723 209.3 462.573 208.13 464.023 208.13C465.603 208.13 466.313 209.3 466.313 210.88C466.313 212.49 465.483 213.63 464.023 213.63ZM464.023 208.67C462.993 208.67 462.433 209.53 462.433 210.88C462.433 212.23 462.933 213.08 464.033 213.08C465.093 213.08 465.593 212.23 465.593 210.88C465.593 209.53 465.113 208.67 464.023 208.67ZM471.177 213.5V210.19C471.177 209.75 471.137 209.35 470.947 209.09C470.767 208.84 470.477 208.72 470.067 208.72C468.997 208.72 468.517 209.51 468.517 210.41V213.5H467.817V208.26H468.467V209.19H468.487C468.767 208.6 469.357 208.13 470.227 208.13C471.357 208.13 471.867 208.74 471.867 209.94V213.5H471.177ZM474.074 206.69H474.724V208.26H476.064V208.8H474.724V212.19C474.724 212.75 474.964 212.99 475.564 212.99C475.864 212.99 476.164 212.93 476.164 212.93V213.51C476.164 213.51 475.864 213.59 475.394 213.59C474.384 213.59 474.034 213.05 474.034 212.27V208.8H473.034V208.28H473.264C473.934 208.28 474.074 208.13 474.074 207.17V206.69ZM480.757 213.5V210.18C480.757 209.75 480.717 209.35 480.527 209.09C480.347 208.84 480.057 208.72 479.647 208.72C478.567 208.72 478.097 209.52 478.097 210.41V213.5H477.397V205.62H478.097V209.18H478.117C478.357 208.6 478.937 208.13 479.807 208.13C480.937 208.13 481.447 208.74 481.447 209.94V213.5H480.757ZM486.815 209.52H486.145C486.065 209.01 485.635 208.67 484.925 208.67C484.205 208.67 483.755 209.08 483.755 209.61C483.755 210.12 484.045 210.36 484.575 210.43L485.515 210.56C486.515 210.7 486.925 211.21 486.925 212.01C486.925 213 486.115 213.63 484.925 213.63C483.865 213.63 483.005 213.2 482.865 212.03H483.545C483.655 212.65 484.045 213.08 484.935 213.08C485.745 213.08 486.245 212.66 486.245 212.07C486.245 211.6 486.095 211.3 485.335 211.19L484.475 211.07C483.595 210.95 483.095 210.53 483.095 209.67C483.095 208.75 483.845 208.13 484.945 208.13C486.035 208.13 486.725 208.64 486.815 209.52Z"
            fill={theme.colors.neutral[700]}
          />
          <path
            d="M566.894 156.5C567.17 156.5 567.394 156.724 567.394 157C567.394 157.276 567.17 157.5 566.894 157.5V156.5ZM564.416 157.495C564.139 157.494 563.916 157.269 563.918 156.993C563.919 156.717 564.143 156.494 564.42 156.495L564.416 157.495ZM559.496 156.456C559.772 156.459 559.993 156.686 559.99 156.962C559.987 157.238 559.76 157.459 559.484 157.456L559.496 156.456ZM554.553 157.379C554.277 157.373 554.058 157.145 554.063 156.869C554.069 156.593 554.297 156.373 554.573 156.379L554.553 157.379ZM549.651 156.263C549.927 156.27 550.144 156.5 550.137 156.776C550.129 157.052 549.899 157.27 549.623 157.262L549.651 156.263ZM544.694 157.108C544.418 157.098 544.202 156.867 544.212 156.591C544.221 156.315 544.453 156.099 544.729 156.108L544.694 157.108ZM539.808 155.916C540.084 155.928 540.298 156.161 540.286 156.437C540.274 156.713 540.041 156.927 539.765 156.915L539.808 155.916ZM534.838 156.684C534.562 156.67 534.35 156.435 534.364 156.159C534.378 155.883 534.613 155.671 534.888 155.685L534.838 156.684ZM529.97 155.416C530.246 155.432 530.456 155.668 530.44 155.944C530.424 156.22 530.187 156.43 529.912 156.414L529.97 155.416ZM524.988 156.106C524.713 156.087 524.504 155.849 524.523 155.573C524.541 155.298 524.779 155.089 525.055 155.108L524.988 156.106ZM520.144 154.761C520.419 154.782 520.626 155.022 520.605 155.297C520.585 155.572 520.345 155.779 520.069 155.759L520.144 154.761ZM515.158 155.373C514.883 155.35 514.678 155.109 514.701 154.833C514.724 154.558 514.965 154.353 515.24 154.376L515.158 155.373ZM510.342 153.952C510.617 153.977 510.82 154.22 510.795 154.495C510.77 154.77 510.526 154.973 510.251 154.948L510.342 153.952ZM505.327 154.48C505.053 154.453 504.852 154.208 504.879 153.933C504.907 153.659 505.151 153.458 505.426 153.485L505.327 154.48ZM500.531 152.979C500.805 153.008 501.004 153.255 500.974 153.53C500.945 153.804 500.698 154.003 500.424 153.973L500.531 152.979ZM495.525 153.424C495.25 153.392 495.054 153.143 495.086 152.869C495.118 152.595 495.366 152.398 495.641 152.43L495.525 153.424ZM490.752 151.837C491.026 151.872 491.22 152.122 491.185 152.396C491.151 152.67 490.901 152.864 490.627 152.83L490.752 151.837ZM485.728 152.188C485.454 152.151 485.262 151.899 485.3 151.625C485.337 151.352 485.589 151.16 485.862 151.197L485.728 152.188ZM480.987 150.508C481.261 150.548 481.45 150.802 481.409 151.075C481.369 151.348 481.115 151.537 480.842 151.497L480.987 150.508ZM475.985 150.753C475.712 150.71 475.527 150.454 475.57 150.181C475.614 149.908 475.87 149.722 476.143 149.766L475.985 150.753ZM471.268 148.949C471.54 148.997 471.721 149.256 471.673 149.528C471.625 149.8 471.365 149.982 471.093 149.933L471.268 148.949ZM566.894 157.5C566.064 157.5 565.238 157.498 564.416 157.495L564.42 156.495C565.241 156.498 566.066 156.5 566.894 156.5V157.5ZM559.484 157.456C557.824 157.437 556.181 157.41 554.553 157.379L554.573 156.379C556.198 156.411 557.839 156.437 559.496 156.456L559.484 157.456ZM549.623 157.262C547.961 157.217 546.318 157.165 544.694 157.108L544.729 156.108C546.351 156.166 547.991 156.217 549.651 156.263L549.623 157.262ZM539.765 156.915C538.1 156.843 536.457 156.766 534.838 156.684L534.888 155.685C536.506 155.767 538.146 155.844 539.808 155.916L539.765 156.915ZM529.912 156.414C528.243 156.316 526.602 156.213 524.988 156.106L525.055 155.108C526.666 155.215 528.305 155.318 529.97 155.416L529.912 156.414ZM520.069 155.759C518.399 155.634 516.761 155.505 515.158 155.373L515.24 154.376C516.841 154.508 518.476 154.637 520.144 154.761L520.069 155.759ZM510.251 154.948C508.567 154.795 506.925 154.639 505.327 154.48L505.426 153.485C507.021 153.643 508.66 153.799 510.342 153.952L510.251 154.948ZM500.424 153.973C498.731 153.791 497.097 153.607 495.525 153.424L495.641 152.43C497.21 152.613 498.841 152.797 500.531 152.979L500.424 153.973ZM490.627 152.83C488.901 152.612 487.267 152.397 485.728 152.188L485.862 151.197C487.399 151.406 489.03 151.62 490.752 151.837L490.627 152.83ZM480.842 151.497C479.032 151.231 477.41 150.981 475.985 150.753L476.143 149.766C477.564 149.993 479.182 150.242 480.987 150.508L480.842 151.497ZM471.093 149.933C470.304 149.794 469.703 149.683 469.299 149.607C469.097 149.57 468.945 149.54 468.842 149.521C468.791 149.511 468.752 149.503 468.727 149.498C468.714 149.496 468.704 149.494 468.697 149.493C468.694 149.492 468.691 149.491 468.69 149.491C468.689 149.491 468.688 149.491 468.688 149.491C468.687 149.491 468.687 149.491 468.687 149.491C468.687 149.491 468.687 149.491 468.687 149.491C468.687 149.491 468.687 149.491 468.687 149.49C468.687 149.49 468.687 149.49 468.784 149C468.881 148.51 468.881 148.51 468.881 148.51C468.881 148.51 468.881 148.51 468.881 148.51C468.881 148.51 468.881 148.51 468.881 148.51C468.881 148.51 468.881 148.51 468.881 148.51C468.882 148.51 468.882 148.51 468.883 148.51C468.885 148.51 468.887 148.511 468.89 148.511C468.896 148.513 468.906 148.514 468.918 148.517C468.943 148.522 468.981 148.529 469.032 148.539C469.132 148.558 469.283 148.587 469.484 148.625C469.884 148.7 470.482 148.81 471.268 148.949L471.093 149.933ZM566.894 156.5C567.17 156.5 567.394 156.724 567.394 157C567.394 157.276 567.17 157.5 566.894 157.5V156.5ZM564.416 157.495C564.139 157.494 563.916 157.269 563.918 156.993C563.919 156.717 564.143 156.494 564.42 156.495L564.416 157.495ZM559.496 156.456C559.772 156.459 559.993 156.686 559.99 156.962C559.987 157.238 559.76 157.459 559.484 157.456L559.496 156.456ZM554.553 157.379C554.277 157.373 554.058 157.145 554.063 156.869C554.069 156.593 554.297 156.373 554.573 156.379L554.553 157.379ZM549.651 156.263C549.927 156.27 550.144 156.5 550.137 156.776C550.129 157.052 549.899 157.27 549.623 157.262L549.651 156.263ZM544.694 157.108C544.418 157.098 544.202 156.867 544.212 156.591C544.221 156.315 544.453 156.099 544.729 156.108L544.694 157.108ZM539.808 155.916C540.084 155.928 540.298 156.161 540.286 156.437C540.274 156.713 540.041 156.927 539.765 156.915L539.808 155.916ZM534.838 156.684C534.562 156.67 534.35 156.435 534.364 156.159C534.378 155.883 534.613 155.671 534.888 155.685L534.838 156.684ZM529.97 155.416C530.246 155.432 530.456 155.668 530.44 155.944C530.424 156.22 530.187 156.43 529.912 156.414L529.97 155.416ZM524.988 156.106C524.713 156.087 524.504 155.849 524.523 155.573C524.541 155.298 524.779 155.089 525.055 155.108L524.988 156.106ZM520.144 154.761C520.419 154.782 520.626 155.022 520.605 155.297C520.585 155.572 520.345 155.779 520.069 155.759L520.144 154.761ZM515.158 155.373C514.883 155.35 514.678 155.109 514.701 154.833C514.724 154.558 514.965 154.353 515.24 154.376L515.158 155.373ZM510.342 153.952C510.617 153.977 510.82 154.22 510.795 154.495C510.77 154.77 510.526 154.973 510.251 154.948L510.342 153.952ZM505.327 154.48C505.053 154.453 504.852 154.208 504.879 153.933C504.907 153.659 505.151 153.458 505.426 153.485L505.327 154.48ZM500.531 152.979C500.805 153.008 501.004 153.255 500.974 153.53C500.945 153.804 500.698 154.003 500.424 153.973L500.531 152.979ZM495.525 153.424C495.25 153.392 495.054 153.143 495.086 152.869C495.118 152.595 495.366 152.398 495.641 152.43L495.525 153.424ZM490.752 151.837C491.026 151.872 491.22 152.122 491.185 152.396C491.151 152.67 490.901 152.864 490.627 152.83L490.752 151.837ZM485.728 152.188C485.454 152.151 485.262 151.899 485.3 151.625C485.337 151.352 485.589 151.16 485.862 151.197L485.728 152.188ZM480.987 150.508C481.261 150.548 481.45 150.802 481.409 151.075C481.369 151.348 481.115 151.537 480.842 151.497L480.987 150.508ZM475.985 150.753C475.712 150.71 475.527 150.454 475.57 150.181C475.614 149.908 475.87 149.722 476.143 149.766L475.985 150.753ZM471.268 148.949C471.54 148.997 471.721 149.256 471.673 149.528C471.625 149.8 471.365 149.982 471.093 149.933L471.268 148.949ZM566.894 157.5C566.064 157.5 565.238 157.498 564.416 157.495L564.42 156.495C565.241 156.498 566.066 156.5 566.894 156.5V157.5ZM559.484 157.456C557.824 157.437 556.181 157.41 554.553 157.379L554.573 156.379C556.198 156.411 557.839 156.437 559.496 156.456L559.484 157.456ZM549.623 157.262C547.961 157.217 546.318 157.165 544.694 157.108L544.729 156.108C546.351 156.166 547.991 156.217 549.651 156.263L549.623 157.262ZM539.765 156.915C538.1 156.843 536.457 156.766 534.838 156.684L534.888 155.685C536.506 155.767 538.146 155.844 539.808 155.916L539.765 156.915ZM529.912 156.414C528.243 156.316 526.602 156.213 524.988 156.106L525.055 155.108C526.666 155.215 528.305 155.318 529.97 155.416L529.912 156.414ZM520.069 155.759C518.399 155.634 516.761 155.505 515.158 155.373L515.24 154.376C516.841 154.508 518.476 154.637 520.144 154.761L520.069 155.759ZM510.251 154.948C508.567 154.795 506.925 154.639 505.327 154.48L505.426 153.485C507.021 153.643 508.66 153.799 510.342 153.952L510.251 154.948ZM500.424 153.973C498.731 153.791 497.097 153.607 495.525 153.424L495.641 152.43C497.21 152.613 498.841 152.797 500.531 152.979L500.424 153.973ZM490.627 152.83C488.901 152.612 487.267 152.397 485.728 152.188L485.862 151.197C487.399 151.406 489.03 151.62 490.752 151.837L490.627 152.83ZM480.842 151.497C479.032 151.231 477.41 150.981 475.985 150.753L476.143 149.766C477.564 149.993 479.182 150.242 480.987 150.508L480.842 151.497ZM471.093 149.933C470.304 149.794 469.703 149.683 469.299 149.607C469.097 149.57 468.945 149.54 468.842 149.521C468.791 149.511 468.752 149.503 468.727 149.498C468.714 149.496 468.704 149.494 468.697 149.493C468.694 149.492 468.691 149.491 468.69 149.491C468.689 149.491 468.688 149.491 468.688 149.491C468.687 149.491 468.687 149.491 468.687 149.491C468.687 149.491 468.687 149.491 468.687 149.491C468.687 149.491 468.687 149.491 468.687 149.49C468.687 149.49 468.687 149.49 468.784 149C468.881 148.51 468.881 148.51 468.881 148.51C468.881 148.51 468.881 148.51 468.881 148.51C468.881 148.51 468.881 148.51 468.881 148.51C468.881 148.51 468.881 148.51 468.881 148.51C468.882 148.51 468.882 148.51 468.883 148.51C468.885 148.51 468.887 148.511 468.89 148.511C468.896 148.513 468.906 148.514 468.918 148.517C468.943 148.522 468.981 148.529 469.032 148.539C469.132 148.558 469.283 148.587 469.484 148.625C469.884 148.7 470.482 148.81 471.268 148.949L471.093 149.933Z"
            fill={chartColor}
          />
          <path
            opacity="0.5"
            d="M466.5 147V202L19.4146 202L19.4146 41L51.4998 66L91.4998 61.5L120.5 69L178 72.5L219 87L285 95.5L319 114.5L394 125.5L440 140L466.5 147Z"
            fill="url(#paint0_linear_77_4655)"
          />
          <path
            d="M19.4998 41L51.4998 66L91.4998 61.5L120.137 69.2786L178.292 72.3357L218.611 87.6214L284.621 95.2643L319.249 114.371L394.997 125.836L467.5 148"
            stroke={chartColor}
            strokeWidth="1.5"
          />
          <path
            d="M19.4998 41L54.1431 45.8125L114.313 47.0156L161.72 52.6302L226.752 57.8437L280.844 69.875L333.113 75.0885L378.696 91.5312L440.082 101.156L566.5 118"
            stroke={theme.colors.primary[300]}
            strokeLinejoin="round"
            strokeDasharray="5 5"
          />
          <g>
            <circle
              cx="467.094"
              cy="149"
              r="14"
              fill={chartColor}
              opacity={0.25}
            />
            <circle cx="467.094" cy="149" r="8" fill={dotColor} />
            <circle
              cx="467.094"
              cy="149"
              r="6.75"
              stroke={chartColor}
              strokeWidth="2.5"
            />
          </g>
          <g>
            <circle cx="21" cy="40" r="14" fill={chartColor} opacity={0.25} />
            <circle cx="21" cy="40" r="8" fill={dotColor} />
            <circle
              cx="21"
              cy="40"
              r="6.75"
              stroke={chartColor}
              strokeWidth="2.5"
            />
          </g>
          <rect
            x="396"
            y="108"
            width="166"
            height="24"
            rx="4"
            fill={theme.colors.primary[500]}
          />
          <text
            fill="white"
            xmlSpace="preserve"
            style={{
              whiteSpace: 'pre',
            }}
            fontFamily={theme.fonts?.heading}
            fontSize={12}
            fontWeight="bold"
            letterSpacing="0em"
            textAnchor="middle"
          >
            <tspan x={480} y={124}>
              <FormattedMessage
                defaultMessage="{predictedWeight} with {brand}"
                description="Predicted weight tooltip"
                values={{
                  predictedWeight: convertedPredictedWeight,
                  brand: config.title,
                }}
              />
            </tspan>
          </text>
          <rect
            x="0.5"
            width="100"
            height="24"
            rx="4"
            fill={initialLabelColor}
          />
          <text
            fill="white"
            xmlSpace="preserve"
            style={{
              whiteSpace: 'pre',
            }}
            fontFamily={theme.fonts?.heading}
            fontSize={12}
            fontWeight="bold"
            letterSpacing="0em"
            textAnchor="middle"
          >
            <tspan x={50} y={16}>
              <FormattedMessage
                defaultMessage="{initialWeight}"
                description="Initial weight tooltip"
                values={{
                  initialWeight: convertedInitialWeight,
                }}
              />
            </tspan>
          </text>
          <rect
            x="376"
            y="53"
            width="128"
            height="24"
            rx="4"
            fill={theme.colors.primary[300]}
          />
          <text
            fill="white"
            xmlSpace="preserve"
            style={{
              whiteSpace: 'pre',
            }}
            fontFamily={theme.fonts?.heading}
            fontSize={12}
            fontWeight="bold"
            letterSpacing="0em"
            textAnchor="middle"
          >
            <tspan x={440} y={69}>
              <FormattedMessage
                defaultMessage="Without {brand}"
                description="Without brand"
                values={{
                  brand: config.title,
                }}
              />
            </tspan>
          </text>
          <defs>
            <linearGradient
              id="paint0_linear_77_4655"
              x1="128.395"
              y1="198.88"
              x2="132.876"
              y2="-77.5126"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor={chartColor} stopOpacity="0" />
              <stop offset="1" stopColor={chartColor} />
            </linearGradient>
          </defs>
        </svg>
        {unitSystem && config.countryCode === 'GB' && (
          <div className="w-2/3 md:w-1/2">
            <ToggleTabs
              options={[
                {
                  id: 'METRIC',
                  label: 'kg',
                },
                {
                  id: 'IMPERIAL',
                  label: 'st/lb',
                },
              ]}
              initialSelected={unitSystem}
              onChange={(id: string): void => {
                if (id === 'IMPERIAL') {
                  setUnitSystem('IMPERIAL');
                }
                if (id === 'METRIC') {
                  setUnitSystem('METRIC');
                }
              }}
            />
          </div>
        )}
        {props.showDisclaimer && (
          <div className="flex flex-col gap-2 text-neutral-700">
            <Typography size="paragraph" inheritColor>
              <FormattedMessage
                defaultMessage="Based on a study of a group of our patients who lost an average of 13.7% body weight in 4 months."
                description="Weight loss graph peer reviewed study"
              />
            </Typography>
            <Typography size="paragraph" isItalic inheritColor>
              <FormattedMessage
                defaultMessage="DOI: 10.1089/tmr.2024.0058"
                description="DOI"
              />
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};
